import { NextAuthOptions } from "next-auth";
import { JWT } from "next-auth/jwt";
import CredentialsProvider from "next-auth/providers/credentials";
import { jwtDecode } from "jwt-decode";

const SESSION_MAX_AGE = 30 * 24 * 60 * 60;
const ACCESS_TOKEN_REFRESH_BUFFER_MS = 60 * 1000;
const ACCESS_TOKEN_REFRESH_RETRY_DELAY_MS = 30 * 1000;

const LOGIN_ERROR = "Invalid username or password";
const REFRESH_ACCESS_TOKEN_ERROR = "RefreshAccessTokenError";
const REFRESH_ACCESS_TOKEN_TRANSIENT_ERROR =
  "RefreshAccessTokenTransientError";
const refreshRequests = new Map<string, Promise<JWT>>();

type DecodedAccessToken = {
  exp?: number;
};

type AuthUser = {
  id?: string;
  username?: string;
  displayName?: string;
};

type AuthTokenResponse = {
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number;
  tokenType?: string;
  user?: AuthUser;
  message?: string;
};

type AuthorizedUser = {
  id: string;
  name: string;
  username: string;
  accessToken: string;
  refreshToken?: string;
  accessTokenExpires?: number;
};

function getAuthApiEndpoint() {
  const endpoint =
    process.env.INTERNAL_API_ENDPOINT || process.env.NEXT_PUBLIC_API_ENDPOINT;

  if (!endpoint) {
    throw new Error("Missing auth API endpoint configuration");
  }

  return endpoint.replace(/\/$/, "");
}

async function parseAuthJsonResponse<T>(response: Response): Promise<T | undefined> {
  const rawResponse = await response.text();

  if (!rawResponse.trim()) {
    return undefined;
  }

  return JSON.parse(rawResponse) as T;
}

function getAccessTokenExpiresAt({
  accessToken,
  expiresAtSeconds,
  expiresInSeconds,
}: {
  accessToken?: string | null;
  expiresAtSeconds?: number | null;
  expiresInSeconds?: number | null;
}) {
  if (typeof expiresAtSeconds === "number" && Number.isFinite(expiresAtSeconds)) {
    return expiresAtSeconds * 1000;
  }

  if (typeof expiresInSeconds === "number" && Number.isFinite(expiresInSeconds)) {
    return Date.now() + expiresInSeconds * 1000;
  }

  if (accessToken) {
    try {
      const decoded = jwtDecode<DecodedAccessToken>(accessToken);
      if (decoded.exp) {
        return decoded.exp * 1000;
      }
    } catch {
      return undefined;
    }
  }

  return undefined;
}

function getRefreshRetryAt() {
  return Date.now() + ACCESS_TOKEN_REFRESH_RETRY_DELAY_MS;
}

async function refreshAccessToken(token: JWT): Promise<JWT> {
  if (!token.refreshToken) {
    return { ...token, error: REFRESH_ACCESS_TOKEN_ERROR, refreshRetryAt: undefined };
  }

  const currentRefreshToken = String(token.refreshToken);
  const inFlightRequest = refreshRequests.get(currentRefreshToken);

  if (inFlightRequest) {
    return inFlightRequest;
  }

  let refreshRequest: Promise<JWT>;

  refreshRequest = (async (): Promise<JWT> => {
    try {
      const response = await fetch(`${getAuthApiEndpoint()}/v1/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refreshToken: currentRefreshToken }),
        cache: "no-store",
      });

      const refreshedTokens = await parseAuthJsonResponse<
        AuthTokenResponse & {
          error?: string;
          error_description?: string;
        }
      >(response);

      if (!response.ok || !refreshedTokens?.accessToken) {
        const isInvalidRefreshToken = [400, 401, 403].includes(response.status);

        if (isInvalidRefreshToken) {
          return {
            ...token,
            error: REFRESH_ACCESS_TOKEN_ERROR,
            refreshRetryAt: undefined,
          };
        }

        throw new Error(
          refreshedTokens?.message ||
            refreshedTokens?.error_description ||
            refreshedTokens?.error ||
            `Refresh request failed with status ${response.status}`
        );
      }

      return {
        ...token,
        name:
          refreshedTokens.user?.displayName ||
          refreshedTokens.user?.username ||
          token.name,
        username: refreshedTokens.user?.username || token.username,
        accessToken: refreshedTokens.accessToken,
        accessTokenExpires: getAccessTokenExpiresAt({
          accessToken: refreshedTokens.accessToken,
          expiresInSeconds: refreshedTokens.expiresIn,
        }),
        refreshToken: refreshedTokens.refreshToken ?? token.refreshToken,
        error: undefined,
        refreshRetryAt: undefined,
      };
    } catch (error) {
      console.error("Failed to refresh access token", error);
      return {
        ...token,
        error: REFRESH_ACCESS_TOKEN_TRANSIENT_ERROR,
        refreshRetryAt: getRefreshRetryAt(),
      };
    } finally {
      refreshRequests.delete(currentRefreshToken);
    }
  })();

  refreshRequests.set(currentRefreshToken, refreshRequest);
  return refreshRequest;
}

export const authOptions: NextAuthOptions = {
  secret: process.env.NEXTAUTH_SECRET,
  providers: [
    CredentialsProvider({
      id: "credentials",
      name: process.env.AUTH_NAME || "Credentials",
      credentials: {
        username: { label: "Username", type: "text" },
        password: { label: "Password", type: "password" },
      },
      async authorize(credentials) {
        const username = credentials?.username?.trim();
        const password = credentials?.password;

        if (!username || !password) {
          throw new Error(LOGIN_ERROR);
        }

        const response = await fetch(`${getAuthApiEndpoint()}/v1/auth/login`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ username, password }),
          cache: "no-store",
        });

        const loginResponse = await parseAuthJsonResponse<AuthTokenResponse>(response);

        if (!response.ok || !loginResponse?.accessToken) {
          throw new Error(loginResponse?.message || LOGIN_ERROR);
        }

        return {
          id: loginResponse.user?.id || username,
          name:
            loginResponse.user?.displayName ||
            loginResponse.user?.username ||
            username,
          username: loginResponse.user?.username || username,
          accessToken: loginResponse.accessToken,
          refreshToken: loginResponse.refreshToken,
          accessTokenExpires: getAccessTokenExpiresAt({
            accessToken: loginResponse.accessToken,
            expiresInSeconds: loginResponse.expiresIn,
          }),
        } satisfies AuthorizedUser;
      },
    }),
  ],
  pages: {
    signIn: "/auth/login",
    signOut: "/auth/signout",
  },
  session: {
    strategy: "jwt",
    maxAge: SESSION_MAX_AGE,
    updateAge: 24 * 60 * 60,
  },
  callbacks: {
    async jwt({ token, user }) {
      if (user) {
        const authorizedUser = user as AuthorizedUser;

        return {
          ...token,
          id: authorizedUser.id,
          name: authorizedUser.name,
          username: authorizedUser.username,
          accessToken: authorizedUser.accessToken,
          refreshToken: authorizedUser.refreshToken,
          accessTokenExpires: authorizedUser.accessTokenExpires,
          error: undefined,
          refreshRetryAt: undefined,
        };
      }

      const accessTokenExpires = getAccessTokenExpiresAt({
        accessToken: token.accessToken as string | undefined,
        expiresAtSeconds:
          typeof token.accessTokenExpires === "number"
            ? Math.floor(token.accessTokenExpires / 1000)
            : undefined,
      });

      const refreshRetryAt =
        typeof token.refreshRetryAt === "number"
          ? token.refreshRetryAt
          : undefined;

      if (
        accessTokenExpires &&
        Date.now() < accessTokenExpires - ACCESS_TOKEN_REFRESH_BUFFER_MS
      ) {
        return {
          ...token,
          accessTokenExpires,
          error:
            token.error === REFRESH_ACCESS_TOKEN_TRANSIENT_ERROR
              ? undefined
              : token.error,
        };
      }

      if (refreshRetryAt && Date.now() < refreshRetryAt) {
        // Still inside the post-failure backoff window and the access token
        // is expired — keep the transient error flag so callers (e.g. the
        // http client's 401 handler) know this token is not safe to retry
        // with, instead of wrongly reporting a healthy session.
        return {
          ...token,
          accessTokenExpires,
          error: token.error,
          refreshRetryAt,
        };
      }

      return refreshAccessToken(token);
    },
    async session({ session, token }) {
      (session as any).accessToken = token.accessToken;
      (session as any).refreshToken = token.refreshToken;
      (session as any).accessTokenExpires = token.accessTokenExpires;
      (session as any).error = token.error;

      session.user = {
        ...session.user,
        id: typeof token.id === "string" ? token.id : undefined,
        name: typeof token.name === "string" ? token.name : undefined,
        username: typeof token.username === "string" ? token.username : undefined,
      } as typeof session.user;

      if (typeof token.exp === "number") {
        session.expires = new Date(token.exp * 1000).toISOString();
      }
      return session;
    },
    async redirect({ url, baseUrl }) {
      if (url.includes("/auth/login")) return `${baseUrl}/`;
      if (url.startsWith("/")) return `${baseUrl}${url}`;
      if (new URL(url).origin === baseUrl) return url;
      return baseUrl;
    },
  },
  events: {},
};
